export type CpfFilterType = "valid" | "invalid" | "all";

export const CPF_FILTER = {
  VALID: "valid" as CpfFilterType,
  INVALID: "invalid" as CpfFilterType,
  ALL: "all" as CpfFilterType,
} as const;

export type StoreHealthStatus = "healthy" | "error";

export interface StoreHealthEntry {
  status: StoreHealthStatus;
  message?: string;
}

/**
 * Identifica qual tab está ativa na tela de Consulta de Acessos.
 * - "users"     → lista de usuários responsáveis (RSP001)
 * - "operators" → lista de operadores (W07USU)
 */
export type AccessTab = "users" | "operators";

/**
 * Conjunto de filtros/estado específico de cada tab. Isolado para permitir
 * que cada tab mantenha search/sort/paginação independentes.
 */
export interface TabFilters {
  search: string;
  /** Busca por código (input dedicado — novo, separado de search). */
  code: string;
  /**
   * Classificação visual derivada dos códigos/tipo:
   *  - Operadores: "Caixa" | "Gerente" | "-"
   *  - Usuários:   "Desconto" | "Caixa" | "Tesouraria" | "-"
   *
   * `null` = sem filtro de classificação.
   */
  classification: string | null;
  cpfFilter: CpfFilterType;
  /**
   * Status de bloqueio da entidade da tab. `null` = todos (bloqueados + desbloqueados).
   * Substitui os antigos `userBlocked` / `operatorBlocked` flat — cada tab usa
   * apenas o seu campo agora.
   */
  blocked: boolean | null;
  /**
   * Filtro pela situação do colaborador no Sênior (Logix). Valor literal
   * vindo do backend (ex: "1. Trabalhando", "7. Demitido"). `null` = sem
   * filtro. Passthrough sem normalização — backend aceita qualquer string.
   */
  situation: string | null;
  sortBy: string;
  sortOrder: "ASC" | "DESC";
  page: number;
  perPage: number;
}

/**
 * Shape do slice — `storeCode` e `activeTab` são GLOBAIS; `users`/`operators`
 * são sub-objetos por-tab para que filtros/paginação não vazem entre tabs.
 */
export interface StoreAccessFilterState {
  /** Loja selecionada (contexto da tela, não filtro). */
  storeCode: number | null;
  /** Tab ativa — preservada em troca de loja. */
  activeTab: AccessTab;
  /** Filtros e paginação da tab Usuários. */
  users: TabFilters;
  /** Filtros e paginação da tab Operadores. */
  operators: TabFilters;
  /** Saúde por loja — estado volátil, NÃO persistido (blacklist em redux-persist). */
  storeHealth: Record<number, StoreHealthEntry>;
}

/**
 * Payload do reducer `setTabFilter` — identifica qual tab recebe a atualização.
 */
export type SetTabFilterPayload<K extends keyof TabFilters = keyof TabFilters> = {
  tab: AccessTab;
  key: K;
  value: TabFilters[K];
};
